/**
 * What the player set down in a room, said when the room is described (D226,
 * E8): the engine's own "here, where you left it" sentence.
 *
 * @remarks
 * R25 gave `drop` a world model — `world.droppedItemLocations` records where
 * each thing was set down, the catalog lists it there, and the room survey
 * (`search`) names it — but the room's DESCRIPTION never knew. A player who
 * set the flask down in the mangroves and typed `look` read a paragraph with
 * no flask in it, and lost track of a thing the world still held. The
 * paragraph is the pack's prose; what the player did to the room is the
 * engine's fact, so the engine speaks it, after the paragraph, on arrival
 * and on `look` alike. No pack authors it, and no pack could: the prose was
 * written before the player existed.
 *
 * The names come in as a sentence list, each with the article it takes
 * (D182/E9), so the one slot reads for a flask or for a flask and a rope.
 * Record insertion order keeps the list deterministic, as the survey's is.
 *
 * @packageDocumentation
 */

import type { ItemId, RoomId, RuntimeState } from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { requireItem } from '../bundle/bundle-lookups.js';
import { nameInSentence, speakSlot, type SpokenResponse } from '../defaults/default-responses.js';

/**
 * The `droppedHere` line for a room, or nothing when the player left nothing
 * there.
 *
 * @param bundle - The loaded bundle
 * @param state - The live runtime state (its record of where things were set down)
 * @param roomId - The room being described — named by the caller, because on arrival the state still stands in the room before
 * @returns The spoken slot, or `undefined` when there is nothing to say
 */
export const droppedHereResponse = (bundle: EngineGameBundle, state: RuntimeState, roomId: RoomId): SpokenResponse | undefined => {
	const droppedNames = Object.entries(state.world.droppedItemLocations)
		.filter(([, droppedRoomId]) => droppedRoomId === roomId)
		.map(([droppedItemId]) => nameInSentence(bundle, requireItem(bundle, droppedItemId as ItemId).name));

	if (droppedNames.length === 0) {
		return undefined;
	}

	return speakSlot(bundle, state, 'droppedHere', { itemNames: listInSentence(droppedNames) });
};

/**
 * Join names as a sentence does: "the flask", "the flask and the rope", "the
 * flask, the rope and the key".
 *
 * @param names - The names, already carrying their articles
 * @returns The list as prose
 */
export const listInSentence = (names: readonly string[]): string =>
	names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names.slice(-1).join('')}`;
